// src/Pages/PharmacistDashboard/MedicationInventory.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as ReorderIcon,
  Delete as RemoveIcon,
  ExpandLess,
  ExpandMore,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { inventoryMockData } from '../../data/pharmacistMedicationManagementMockData';

const MedicationInventory = () => {
  const [inventory, setInventory] = useState(inventoryMockData);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStockStatus, setFilterStockStatus] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterCategoryChange = (event) => {
    setFilterCategory(event.target.value);
  };

  const handleFilterStockStatusChange = (event) => {
    setFilterStockStatus(event.target.value);
  };

  const handleReorder = (id) => {
    setInventory((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, reordering: true } : item
      )
    );
  };

  const handleRemoveExpired = (id) => {
    setInventory((prev) => prev.filter((item) => item.id !== id));
  };

  const handleExpandItem = (id) => {
    setExpandedItem((prev) => (prev === id ? null : id));
  };

  const filteredInventory = inventory
    .filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((item) =>
      filterCategory ? item.category === filterCategory : true
    )
    .filter((item) =>
      filterStockStatus ? item.stockStatus === filterStockStatus : true
    );

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Medication Inventory Management
      </Typography>

      {/* Search and Filters */}
      <Grid container spacing={3} sx={{ marginBottom: 4 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label='Search by Medication Name'
            variant='outlined'
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={filterCategory}
              onChange={handleFilterCategoryChange}
              label='Filter by Category'
            >
              <MenuItem value=''>All Categories</MenuItem>
              <MenuItem value='Mental Health'>Mental Health</MenuItem>
              <MenuItem value='Pain Relievers'>Pain Relievers</MenuItem>
              <MenuItem value='Vitamins'>Vitamins</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filter by Stock Status</InputLabel>
            <Select
              value={filterStockStatus}
              onChange={handleFilterStockStatusChange}
              label='Filter by Stock Status'
            >
              <MenuItem value=''>All Statuses</MenuItem>
              <MenuItem value='In Stock'>In Stock</MenuItem>
              <MenuItem value='Low Stock'>Low Stock</MenuItem>
              <MenuItem value='Out of Stock'>Out of Stock</MenuItem>
              <MenuItem value='Expired'>Expired</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Inventory List */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='h6'>All Medications</Typography>
          <Paper
            elevation={3}
            sx={{ padding: 2, maxHeight: 400, overflow: 'auto' }}
          >
            <List>
              {filteredInventory.map((item) => (
                <div key={item.id}>
                  <ListItem
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <ListItemText
                      primary={`${item.name} (${item.category})`}
                      secondary={`Stock: ${item.stock} | Expiry: ${item.expiry}`}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {item.stockStatus === 'Low Stock' && (
                        <Badge badgeContent='Low' color='error' />
                      )}
                      {item.stockStatus === 'Expired' && (
                        <IconButton
                          color='error'
                          onClick={() => handleRemoveExpired(item.id)}
                        >
                          <WarningIcon />
                        </IconButton>
                      )}
                      <Button
                        variant='contained'
                        color='primary'
                        startIcon={<ReorderIcon />}
                        onClick={() => handleReorder(item.id)}
                        disabled={
                          item.reordering || item.stockStatus === 'Out of Stock'
                        }
                      >
                        {item.reordering ? 'Reordering...' : 'Reorder'}
                      </Button>
                      <IconButton onClick={() => handleExpandItem(item.id)}>
                        {expandedItem === item.id ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Collapse
                    in={expandedItem === item.id}
                    timeout='auto'
                    unmountOnExit
                  >
                    <Box sx={{ padding: 2, backgroundColor: '#f5f5f5' }}>
                      <Typography variant='body2'>
                        Detailed Information:{' '}
                        {item.details || 'No details available'}
                      </Typography>
                      <Typography variant='body2'>
                        Last Reorder: {item.reorderHistory || 'No reorders'}
                      </Typography>
                    </Box>
                  </Collapse>
                </div>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MedicationInventory;
